import { t } from "../lib/i18n";
import React, { useState, useEffect } from 'react';
import api from '../api';
import { dispatchLeaseChangeEvent } from '../leaseEventSystem';

interface Lease {
  id: string;
  custom_id?: string;
  tenant_name: string;
  start_date: string;
  end_date: string;
  rent_amount: number;
}

interface EditLeaseModalProps {
  lease: Lease | null;
  onClose: () => void;
}

const EditLeaseModal: React.FC<EditLeaseModalProps> = ({ lease, onClose }) => {
  const [tenantName, setTenantName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [rentAmount, setRentAmount] = useState('');
  const [customId, setCustomId] = useState(''); // New state for custom_id
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (lease) {
      setTenantName(lease.tenant_name);
      setStartDate(lease.start_date ? new Date(lease.start_date).toISOString().split('T')[0] : '');
      setEndDate(lease.end_date ? new Date(lease.end_date).toISOString().split('T')[0] : '');
      setRentAmount(lease.rent_amount?.toString() || '');
      setCustomId(lease.custom_id || ''); // Initialize custom_id
    }
  }, [lease]);

  const handleSave = async () => {
    if (!lease) return;

    try {
      await api.put(`/leases/${lease.id}`, {
        tenant_name: tenantName,
        start_date: startDate,
        end_date: endDate,
        rent_amount: parseFloat(rentAmount),
        custom_id: customId, // Include custom_id in the update payload
      });
      setMessage('Lease updated successfully!');
      dispatchLeaseChangeEvent();
      onClose();
    } catch (error: any) {
      console.error('Error updating lease:', error);
      setMessage(error.response?.data?.detail || 'Failed to update lease.');
    }
  };

  if (!lease) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-bg-secondary p-6 rounded-lg shadow-lg w-full max-w-md">
        <h2 className="text-2xl font-bold text-text-primary mb-4">{t("editLeaseModal.k1")}</h2>
        {message && <p className="text-danger mb-4">{message}</p>}
        <div className="mb-4">
          <label htmlFor="customId" className="block text-text-secondary text-sm font-bold mb-2">{t("editLeaseModal.k2")}</label>
          <input
            type="text"
            id="customId"
            value={customId}
            onChange={(e) => setCustomId(e.target.value)}
            className="shadow appearance-none border rounded w-full py-2 px-3 text-text-primary leading-tight focus:outline-none focus:shadow-outline"
          />
        </div>
        <div className="mb-4">
          <label htmlFor="tenantName" className="block text-text-secondary text-sm font-bold mb-2">{t("editLeaseModal.k3")}</label>
          <input
            type="text"
            id="tenantName"
            value={tenantName}
            onChange={(e) => setTenantName(e.target.value)}
            className="shadow appearance-none border rounded w-full py-2 px-3 text-text-primary leading-tight focus:outline-none focus:shadow-outline"
          />
        </div>
        <div className="mb-4">
          <label htmlFor="startDate" className="block text-text-secondary text-sm font-bold mb-2">{t("editLeaseModal.k4")}</label>
          <input
            type="date"
            id="startDate"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="shadow appearance-none border rounded w-full py-2 px-3 text-text-primary leading-tight focus:outline-none focus:shadow-outline"
          />
        </div>
        <div className="mb-4">
          <label htmlFor="endDate" className="block text-text-secondary text-sm font-bold mb-2">{t("editLeaseModal.k5")}</label>
          <input
            type="date"
            id="endDate"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="shadow appearance-none border rounded w-full py-2 px-3 text-text-primary leading-tight focus:outline-none focus:shadow-outline"
          />
        </div>
        <div className="mb-4">
          <label htmlFor="rentAmount" className="block text-text-secondary text-sm font-bold mb-2">{t("editLeaseModal.k6")}</label>
          <input
            type="number"
            id="rentAmount"
            value={rentAmount}
            onChange={(e) => setRentAmount(e.target.value)}
            className="shadow appearance-none border rounded w-full py-2 px-3 text-text-primary leading-tight focus:outline-none focus:shadow-outline"
          />
        </div>
        <div className="flex justify-end gap-4">
          <button onClick={onClose} className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600">
            {t("editLeaseModal.k7")} </button>
          <button onClick={handleSave} className="bg-primary text-white px-4 py-2 rounded hover:bg-primary-dark">
            {t("editLeaseModal.k8")} </button>
        </div>
      </div>
    </div>
  );
};

export default EditLeaseModal;
